import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  DollarSign,
  Calendar,
  Users,
  Repeat,
  Activity,
  Clock,
  Tag,
  MapPin,
  Gauge,
  CalendarCheck,
  Ruler,
  RefreshCw,
} from "lucide-react";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString();

function Delta({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${up ? "text-emerald-700" : "text-rose-700"}`}>
      <Icon className="w-3 h-3 mr-0.5" />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function Kpi({ icon: Icon, label, value, sublabel, delta, testid }) {
  return (
    <Card className="p-5 border-border bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Icon className="w-4 h-4" />
        </div>
        {delta !== undefined && <Delta pct={delta} />}
      </div>
      <p className="overline text-muted-foreground mt-4">{label}</p>
      <p className="font-display text-3xl font-semibold tracking-tight mt-1">{value}</p>
      {sublabel && <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>}
    </Card>
  );
}

function Sparkline({ data, dataKey = "revenue", width = 600, height = 80 }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map((d) => d[dataKey])) || 1;
  const step = width / Math.max(data.length - 1, 1);
  const points = data
    .map((d, i) => `${i * step},${height - (d[dataKey] / max) * height}`)
    .join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-20" preserveAspectRatio="none">
      <polygon points={areaPoints} fill="hsl(var(--primary) / 0.12)" />
      <polyline points={points} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
    </svg>
  );
}

function Bar({ label, value, max, suffix, testid }) {
  const pct = max ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 text-sm" data-testid={testid}>
      <span className="w-14 text-muted-foreground shrink-0">{label}</span>
      <div className="flex-1 h-6 bg-muted rounded-md overflow-hidden">
        <div
          className="h-full bg-primary/85 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-20 text-right font-medium tabular-nums">{value}{suffix || ""}</span>
    </div>
  );
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics", { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div data-testid="analytics-loading" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  const h = data.headline;
  const l = data.lifetime;
  const maxTierRev = Math.max(...data.tier_breakdown.map((t) => t.revenue), 1);
  const maxDowBookings = Math.max(...data.day_of_week.map((d) => d.bookings), 1);
  const maxPromoUses = Math.max(...data.promo_usage.map((p) => p.uses), 1);
  const maxSlotBookings = Math.max(...data.slot_popularity.map((s) => s.bookings), 1);

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground">
            Last updated {format(new Date(data.generated_at), "MMM d, h:mm a")}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="analytics-refresh" className="rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
        </Button>
      </div>

      {/* Headline KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={DollarSign} label="Revenue · last 30d"
          value={money(h.revenue_30d)} delta={h.revenue_delta_pct}
          sublabel={`vs ${money(h.revenue_prev_30d)} prior 30d`}
          testid="kpi-revenue"
        />
        <Kpi
          icon={Calendar} label="Bookings · last 30d"
          value={num(h.bookings_30d)} delta={h.bookings_delta_pct}
          sublabel={`vs ${h.bookings_prev_30d} prior 30d`}
          testid="kpi-bookings"
        />
        <Kpi
          icon={TrendingUp} label="Avg. booking value"
          value={money(h.avg_booking_value)}
          sublabel={`${num(h.total_sqft_30d)} sqft cleaned`}
          testid="kpi-aov"
        />
        <Kpi
          icon={Repeat} label="Subscriptions on file"
          value={num(h.active_subscriptions)}
          sublabel={`${money(h.subscription_mrr)}/mo est. recurring`}
          testid="kpi-subs"
        />
        <Kpi
          icon={Users} label="Lifetime customers"
          value={num(l.total_customers)}
          sublabel={`${l.repeat_customers} repeats · ${l.repeat_rate_pct}% rate`}
          testid="kpi-customers"
        />
        <Kpi
          icon={DollarSign} label="Lifetime revenue"
          value={money(l.total_revenue)}
          sublabel={`${num(l.total_bookings)} total jobs`}
          testid="kpi-lifetime-revenue"
        />
        <Kpi
          icon={Gauge} label="Capacity used · 30d"
          value={`${h.capacity_utilization}%`}
          sublabel="of available slots booked"
          testid="kpi-capacity"
        />
        <Kpi
          icon={CalendarCheck} label="Calendar sync rate"
          value={`${h.calendar_sync_rate}%`}
          sublabel="bookings hitting Google Calendar"
          testid="kpi-sync"
        />
      </div>

      {/* Trend chart */}
      <Card className="p-6 border-border bg-white" data-testid="trend-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="overline text-muted-foreground">Revenue · last 30 days</p>
            <p className="font-display text-2xl font-semibold mt-1">{money(h.revenue_30d)}</p>
          </div>
          <Badge variant="outline" className="rounded-full">
            <Activity className="w-3 h-3 mr-1" /> {num(h.bookings_30d)} bookings
          </Badge>
        </div>
        <Sparkline data={data.daily_trend_30d} dataKey="revenue" />
      </Card>

      {/* Tier breakdown + Day of week */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border bg-white" data-testid="tier-breakdown-card">
          <p className="overline text-muted-foreground">Revenue by tier · all time</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">Where the money comes from</h3>
          <div className="space-y-3">
            {data.tier_breakdown.length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
            {data.tier_breakdown.map((t) => (
              <div key={t.tier}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{t.tier}</span>
                  <span className="text-muted-foreground">{t.bookings} jobs · avg {num(t.avg_sqft)} sqft</span>
                </div>
                <Bar label="" value={Math.round(t.revenue)} max={maxTierRev} suffix="" testid={`tier-bar-${t.tier.toLowerCase()}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-border bg-white" data-testid="dow-card">
          <p className="overline text-muted-foreground">Bookings by day of week</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">When customers want service</h3>
          <div className="space-y-2">
            {data.day_of_week.map((d) => (
              <Bar key={d.day} label={d.day} value={d.bookings} max={maxDowBookings} testid={`dow-bar-${d.day}`} />
            ))}
          </div>
        </Card>
      </div>

      {/* Slot popularity + Promo usage */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border bg-white" data-testid="slot-card">
          <p className="overline text-muted-foreground"><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />Slot popularity</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">Most-booked start times</h3>
          <div className="space-y-2">
            {data.slot_popularity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No bookings yet.</p>
            ) : (
              data.slot_popularity.map((s) => (
                <Bar key={s.slot} label={s.slot} value={s.bookings} max={maxSlotBookings} testid={`slot-bar-${s.slot}`} />
              ))
            )}
          </div>
        </Card>

        <Card className="p-6 border-border bg-white" data-testid="promo-card">
          <p className="overline text-muted-foreground"><Tag className="w-3 h-3 inline -mt-0.5 mr-1" />Promo code usage</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">{l.promo_booking_share_pct}% of bookings used a code</h3>
          <div className="space-y-3">
            {data.promo_usage.length === 0 ? (
              <p className="text-sm text-muted-foreground">No promo codes used yet.</p>
            ) : (
              data.promo_usage.map((p) => (
                <div key={p.code}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{p.code}</span>
                    <span className="text-muted-foreground">-{money(p.total_discount)} given</span>
                  </div>
                  <Bar label="" value={p.uses} max={maxPromoUses} suffix=" uses" testid={`promo-bar-${p.code}`} />
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Subscriptions + Referrals */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border bg-white" data-testid="subs-summary-card">
          <p className="overline text-muted-foreground"><Repeat className="w-3 h-3 inline -mt-0.5 mr-1" />Subscriptions</p>
          <h3 className="font-display text-xl font-semibold mt-1">{data.subscription_summary.active} active plans</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {money(data.subscription_summary.estimated_annual)} estimated annual · {money(data.subscription_summary.monthly_recurring_estimate)}/mo
          </p>
          <div className="mt-4 space-y-2">
            {data.subscription_summary.by_plan.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active subscriptions yet.</p>
            ) : (
              data.subscription_summary.by_plan.map((p) => (
                <div key={p.plan} className="flex justify-between text-sm">
                  <span>{p.plan}</span>
                  <Badge variant="outline">{p.count}</Badge>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="p-6 border-border bg-white" data-testid="referrals-summary-card">
          <p className="overline text-muted-foreground">Referral credits</p>
          <h3 className="font-display text-xl font-semibold mt-1">{money(data.referral_summary.pending_total)} pending</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {data.referral_summary.pending_count} credits waiting · {data.referral_summary.applied_count} applied ({money(data.referral_summary.applied_total)})
          </p>
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">Top referrers</p>
            {data.top_referrers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No referrers yet.</p>
            ) : (
              <div className="space-y-2">
                {data.top_referrers.map((r, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="truncate pr-2">{r.name || r.address || "—"}</span>
                    <span className="text-muted-foreground shrink-0">{r.referrals} ref · {money(r.credits)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Upcoming + Areas */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border bg-white" data-testid="upcoming-card">
          <p className="overline text-muted-foreground">Upcoming · next 7 days</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">{data.upcoming_bookings.length} on the books</h3>
          {data.upcoming_bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings in the next 7 days.</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_bookings.map((b) => (
                <div key={b.id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{b.customer_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {format(new Date(b.date), "EEE MMM d")} · {b.time_slot} · {b.tier}
                    </p>
                  </div>
                  <span className="font-display text-base font-semibold shrink-0 ml-3">{money(b.final_total)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-border bg-white" data-testid="areas-card">
          <p className="overline text-muted-foreground"><MapPin className="w-3 h-3 inline -mt-0.5 mr-1" />Top neighborhoods</p>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">Where you work most</h3>
          {data.top_areas.length === 0 ? (
            <p className="text-sm text-muted-foreground">Not enough booking history yet.</p>
          ) : (
            <div className="space-y-2">
              {data.top_areas.map((a, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0">
                  <span className="truncate pr-2">{a.area}</span>
                  <Badge variant="outline">{a.bookings} jobs</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick stats footer */}
      <Card className="p-6 border-border bg-muted/40" data-testid="quick-stats">
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <p className="overline text-muted-foreground"><Ruler className="w-3 h-3 inline -mt-0.5 mr-1" />Avg lead time</p>
            <p className="font-display text-2xl font-semibold mt-1">{l.avg_lead_time_days} days</p>
            <p className="text-xs text-muted-foreground">between booking and service</p>
          </div>
          <div>
            <p className="overline text-muted-foreground">Status breakdown</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.status_breakdown.map((s) => (
                <Badge key={s.status} variant="outline" className="capitalize">
                  {s.status}: {s.count}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <p className="overline text-muted-foreground">Promo-driven revenue</p>
            <p className="font-display text-2xl font-semibold mt-1">{money(data.promo_usage.reduce((s, p) => s + p.revenue, 0))}</p>
            <p className="text-xs text-muted-foreground">from {data.promo_usage.reduce((s, p) => s + p.uses, 0)} discounted bookings</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
